import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  CircularProgress, 
  Alert, 
  Divider,
  Chip,
  Stack,
  Tooltip
} from '@mui/material';
import { format } from 'date-fns';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import PendingIcon from '@mui/icons-material/Pending';
import InfoIcon from '@mui/icons-material/Info';
import HistoryIcon from '@mui/icons-material/History';
import RefreshIcon from '@mui/icons-material/Refresh';
import api from '../../utils/api';

interface DbsManagementProps {
  counsellorId: string;
  isAdmin?: boolean;
  refreshCounsellor?: () => void;
  token: string;
}

interface DbsStatus {
  status: 'BLANK_NO_NEW_INFO' | 'NON_BLANK_NO_NEW_INFO' | 'NEW_INFO' | 'NO_MATCH_FOUND' | 'pending' | 'failed';
  lastChecked: string | null;
  nextCheckDate: string | null;
  dbsType?: string;
  dbsUpdateServiceId?: string;
  dbsNumber?: string;
  dbsExpiryDate?: string | null;
  dbsCertificatePrintDate?: string | null;
  certificate: {
    number: string;
    issueDate: string;
  } | null;
  lastCheckResult?: {
    success: boolean;
    error?: string;
    timestamp?: string;
  };
}

// Bug fix: this component previously showed a hardcoded "DBS Certificate
// Valid" banner for every certificate-type DBS, regardless of whether it
// had actually expired. Thresholds mirror utils/complianceChecker.ts on
// the server (expired once past the date, expiring within 90 days),
// so the two can never disagree.
type CertificateValidity = 'valid' | 'expiring' | 'expired' | 'unknown';

export function getCertificateValidity(expiryDate: string | null | undefined): { validity: CertificateValidity; daysUntilExpiry: number | null } {
  if (!expiryDate) {
    return { validity: 'unknown', daysUntilExpiry: null };
  }
  const expiry = new Date(expiryDate);
  if (isNaN(expiry.getTime())) {
    return { validity: 'unknown', daysUntilExpiry: null };
  }
  const now = new Date();
  const daysUntilExpiry = Math.ceil((expiry.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  if (daysUntilExpiry < 0) {
    return { validity: 'expired', daysUntilExpiry };
  }
  if (daysUntilExpiry <= 90) {
    return { validity: 'expiring', daysUntilExpiry };
  }
  return { validity: 'valid', daysUntilExpiry };
}

interface DbsHistoryItem {
  checkDate: string;
  status: string;
  checkedBy: string;
  result: any;
}

const DbsManagement: React.FC<DbsManagementProps> = ({ 
  counsellorId, 
  isAdmin = false,
  refreshCounsellor,
  token
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [checkLoading, setCheckLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [dbsStatus, setDbsStatus] = useState<DbsStatus | null>(null);
  const [showHistory, setShowHistory] = useState<boolean>(false);
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [dbsHistory, setDbsHistory] = useState<DbsHistoryItem[]>([]);

  const fetchDbsStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get(`/api/dbs/status/${counsellorId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      setDbsStatus(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch DBS status');
      console.error('Error fetching DBS status:', err);
    } finally {
      setLoading(false);
    }
  }, [counsellorId, token]);

  const fetchDbsHistory = async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    
    try {
      const response = await api.get(`/api/dbs/history/${counsellorId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      setDbsHistory(response.data.history || []);
    } catch (err: any) {
      setHistoryError(err.response?.data?.message || 'Failed to fetch DBS history');
      console.error('Error fetching DBS history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const triggerDbsCheck = async () => {
    if (!isAdmin) return;
    
    setCheckLoading(true);
    setError(null);
    
    try {
      // Get current user info from localStorage to pass as employee name
      const userStr = localStorage.getItem('user');
      const currentUser = userStr ? JSON.parse(userStr) : null;
      const employeeName = currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'Admin User';
      
      await api.post(`/api/dbs/check/${counsellorId}`, {
        employeeName
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      // Refresh DBS status after check
      await fetchDbsStatus();
      
      // If history is shown, refresh it too
      if (showHistory) {
        await fetchDbsHistory();
      }
      
      // If parent component provided a refresh function, call it
      if (refreshCounsellor) {
        refreshCounsellor();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to trigger DBS check');
      console.error('Error triggering DBS check:', err);
    } finally {
      setCheckLoading(false);
    }
  };

  const toggleHistory = async () => {
    const newShowHistory = !showHistory;
    setShowHistory(newShowHistory);
    
    if (newShowHistory && dbsHistory.length === 0) {
      await fetchDbsHistory();
    }
  };

  useEffect(() => {
    fetchDbsStatus();
  }, [fetchDbsStatus]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'BLANK_NO_NEW_INFO':
        return 'success.main';
      case 'NON_BLANK_NO_NEW_INFO':
        return 'warning.main'; // Amber/warning - certificate has content, needs review
      case 'NEW_INFO':
        return 'error.main';
      case 'NO_MATCH_FOUND':
        return 'warning.main';
      case 'pending':
        return 'warning.main';
      case 'failed':
        return 'error.main';
      default:
        return 'text.secondary';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'BLANK_NO_NEW_INFO':
        return <CheckCircleIcon color="success" />;
      case 'NON_BLANK_NO_NEW_INFO':
        return <InfoIcon color="warning" />; // Warning icon - certificate has disclosures
      case 'NO_MATCH_FOUND':
        return <InfoIcon color="warning" />;
      case 'NEW_INFO':
        return <ErrorIcon color="error" />;
      case 'pending':
        return <PendingIcon color="warning" />;
      case 'failed':
        return <ErrorIcon color="error" />;
      default:
        return <InfoIcon color="info" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'BLANK_NO_NEW_INFO':
        return 'Clear - No disclosures';
      case 'NON_BLANK_NO_NEW_INFO':
        return 'Contains disclosures - Review required';
      case 'NEW_INFO':
        return 'New information found - action required';
      case 'NO_MATCH_FOUND':
        return 'No match found - verify details';
      case 'pending':
        return 'Check in progress';
      case 'failed':
        return 'Check failed';
      default:
        return 'Unknown status';
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Not available';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy HH:mm');
    } catch (error) {
      return 'Invalid date';
    }
  };

  return (
    <div className="glass-card p-6 mb-3">
      <Typography variant="h6" className="theme-text-primary" gutterBottom>
        {dbsStatus?.dbsType === 'certificate' ? 'DBS Certificate Status' : 'DBS Update Service Status'}
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box display="flex" justifyContent="center" my={3}>
          <CircularProgress />
        </Box>
      ) : dbsStatus ? (
        <>
          {/* Certificate Type - Simple Display */}
          {dbsStatus.dbsType === 'certificate' ? (
            <Box>
              {(() => {
                const { validity, daysUntilExpiry } = getCertificateValidity(dbsStatus.dbsExpiryDate);

                if (validity === 'expired') {
                  return (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        DBS Certificate Expired
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        This certificate expired {Math.abs(daysUntilExpiry as number)} day{Math.abs(daysUntilExpiry as number) === 1 ? '' : 's'} ago
                        {dbsStatus.dbsExpiryDate ? ` on ${formatDate(dbsStatus.dbsExpiryDate)}` : ''}. A renewed certificate must be uploaded before this counsellor can continue providing counselling services.
                      </Typography>
                    </Alert>
                  );
                }

                if (validity === 'expiring') {
                  return (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        DBS Certificate Expiring Soon
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        This certificate expires in {daysUntilExpiry} day{daysUntilExpiry === 1 ? '' : 's'}
                        {dbsStatus.dbsExpiryDate ? ` (${formatDate(dbsStatus.dbsExpiryDate)})` : ''}. Renewal should begin as soon as possible.
                      </Typography>
                    </Alert>
                  );
                }

                if (validity === 'unknown') {
                  return (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        DBS Certificate Expiry Unknown
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        No expiry date is on file for this certificate, so its validity cannot be confirmed. Please check the Legal Information tab.
                      </Typography>
                    </Alert>
                  );
                }

                return (
                  <Alert severity="success" sx={{ mb: 2 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      DBS Certificate Valid
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      This counsellor has a valid DBS certificate on file, expiring {formatDate(dbsStatus.dbsExpiryDate || null)}.
                    </Typography>
                  </Alert>
                );
              })()}
              
              {dbsStatus.certificate && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255, 255, 255, 0.05)', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 1 }}>
                    <strong>Certificate Number:</strong> {dbsStatus.certificate.number}
                  </Typography>
                  {dbsStatus.dbsCertificatePrintDate && (
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                      <strong>Issue Date:</strong> {formatDate(dbsStatus.dbsCertificatePrintDate)}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          ) : (
            /* Update Service Type - Full Status Display */
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                {getStatusIcon(dbsStatus.status)}
                <Typography variant="body1" sx={{ ml: 1, color: 'rgba(255, 255, 255, 0.9)' }}>
                  <strong>Status:</strong> <span style={{ color: getStatusColor(dbsStatus.status) }}>{getStatusText(dbsStatus.status)}</span>
                </Typography>
              </Box>
              
              {/* Show error details if check failed */}
              {dbsStatus.status === 'failed' && dbsStatus.lastCheckResult && !dbsStatus.lastCheckResult.success && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                    Check Failed
                  </Typography>
                  <Typography variant="body2">
                    <strong>Error:</strong> {dbsStatus.lastCheckResult.error || 'Unknown error'}
                  </Typography>
                  {dbsStatus.lastCheckResult.timestamp && (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      <strong>Time:</strong> {formatDate(dbsStatus.lastCheckResult.timestamp)}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                    This may be due to incorrect details, network issues, or DBS Update Service availability. Please verify the DBS number, Update Service ID, and date of birth are correct.
                  </Typography>
                </Alert>
              )}
              
              <Box sx={{ mb: 2 }}>
                {dbsStatus.dbsCertificatePrintDate && (
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', mb: 1 }}>
                    <strong>DBS Certificate Print Date:</strong> {formatDate(dbsStatus.dbsCertificatePrintDate)}
                  </Typography>
                )}
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                  <strong>Last checked:</strong> {formatDate(dbsStatus.lastChecked)}
                </Typography>
                {dbsStatus.nextCheckDate && (
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                    <strong>Next scheduled check:</strong> {formatDate(dbsStatus.nextCheckDate)}
                  </Typography>
                )}
              </Box>
              
              {dbsStatus.certificate && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                    <strong>Certificate number:</strong> {dbsStatus.certificate.number}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                    <strong>Issue date:</strong> {formatDate(dbsStatus.certificate.issueDate)}
                  </Typography>
                </Box>
              )}
            </>
          )}
          
          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            <Button 
              variant="outlined" 
              startIcon={<HistoryIcon />}
              onClick={toggleHistory}
              sx={{
                color: 'rgba(255, 255, 255, 0.9)',
                borderColor: 'rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)'
                }
              }}
            >
              {showHistory ? 'Hide History' : 'Show History'}
            </Button>
            
            {isAdmin && dbsStatus?.dbsUpdateServiceId && dbsStatus?.dbsType !== 'certificate' && (
              <Button 
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={triggerDbsCheck}
                disabled={checkLoading}
                sx={{
                  backgroundColor: 'rgba(100, 181, 246, 0.2)',
                  color: 'rgba(255, 255, 255, 0.9)',
                  border: '1px solid rgba(100, 181, 246, 0.3)',
                  '&:hover': {
                    backgroundColor: 'rgba(100, 181, 246, 0.3)',
                    borderColor: 'rgba(100, 181, 246, 0.5)'
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'rgba(255, 255, 255, 0.3)'
                  }
                }}
              >
                {checkLoading ? 'Checking...' : 'Check Now'}
              </Button>
            )}
            
            {isAdmin && (!dbsStatus?.dbsUpdateServiceId || dbsStatus?.dbsType === 'certificate') && (
              <Tooltip title="DBS Update Service checks require a DBS Update Service ID. This counsellor has a certificate only.">
                <Alert severity="info" sx={{ mt: 2 }}>
                  DBS Update Service not available - Certificate only
                </Alert>
              </Tooltip>
            )}
          </Stack>
          
          {showHistory && (
            <Box sx={{ mt: 3 }}>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.9)' }} fontWeight="bold" gutterBottom>
                DBS Check History
              </Typography>
              
              {historyError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {historyError}
                </Alert>
              )}
              
              {historyLoading ? (
                <Box display="flex" justifyContent="center" my={2}>
                  <CircularProgress size={24} />
                </Box>
              ) : dbsHistory.length > 0 ? (
                dbsHistory.map((item, index) => (
                  <div 
                    key={index} 
                    className="glass-card-light p-4 mb-2 rounded"
                    style={{ borderLeft: `4px solid ${getStatusColor(item.status)}` }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                        <strong>Date:</strong> {formatDate(item.checkDate)}
                      </Typography>
                      <Chip 
                        icon={getStatusIcon(item.status)} 
                        label={getStatusText(item.status)}
                        size="small"
                        sx={{ 
                          backgroundColor: `${getStatusColor(item.status)}15`,
                          color: getStatusColor(item.status),
                          borderColor: getStatusColor(item.status)
                        }}
                        variant="outlined"
                      />
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1, color: 'rgba(255, 255, 255, 0.9)' }}>
                      <strong>Checked by:</strong> {item.checkedBy}
                    </Typography>
                    {/* Show error details if check failed */}
                    {item.status === 'failed' && item.result && !item.result.success && item.result.error && (
                      <Alert severity="error" sx={{ mt: 2, mb: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Error: {item.result.error}
                        </Typography>
                        {item.result.statusCode && (
                          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                            Status Code: {item.result.statusCode}
                          </Typography>
                        )}
                      </Alert>
                    )}
                  </div>
                ))
              ) : (
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                  No history available
                </Typography>
              )}
            </Box>
          )}
        </>
      ) : (
        <Alert severity="info">
          No DBS Update Service information available. The counsellor may not have opted in to the DBS Update Service.
        </Alert>
      )}
      
      <Box sx={{ mt: 3 }}>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          <InfoIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
          The DBS Update Service allows employers to check if there have been any changes to a DBS certificate since it was issued.
        </Typography>
      </Box>
    </div>
  );
};

export default DbsManagement;
